import api from "@/api/v1-jwt/config/api";
import {
  useQuery,
  QueryFunctionContext,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  bulkInvoiceUploadEntitySchema,
  BulkInvoiceUploadEntity,
} from "../types";
import { invoiceQueryKeys } from "../query-keys";
import { createErrorHandler } from "@/utils/errorHandler";

type BulkUploadEntityQueryKey = ReturnType<
  typeof invoiceQueryKeys.bulkUploads.entity
>;

const fetchBulkInvoiceUploadEntity = async ({
  queryKey,
}: QueryFunctionContext<BulkUploadEntityQueryKey>): Promise<BulkInvoiceUploadEntity> => {
  const [, , , userId, bulkUploadId] = queryKey;
  const url = `/platform/invoices/${userId}/bulk/${bulkUploadId}`;
  const response = await api.get(url);
  return bulkInvoiceUploadEntitySchema.parse(response.data);
};

export const bulkUploadEntity = {
  createOptions: <TSelected = BulkInvoiceUploadEntity>(
    userId: string,
    bulkUploadId: string,
    options?: Omit<
      UseQueryOptions<
        BulkInvoiceUploadEntity,
        Error,
        TSelected,
        BulkUploadEntityQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const defaultOptions: UseQueryOptions<
      BulkInvoiceUploadEntity,
      Error,
      TSelected,
      BulkUploadEntityQueryKey
    > = {
      queryKey: invoiceQueryKeys.bulkUploads.entity(userId, bulkUploadId),
      queryFn: fetchBulkInvoiceUploadEntity,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: createErrorHandler(),
    };
    return { ...defaultOptions, ...options };
  },
  useQuery: <TSelected = BulkInvoiceUploadEntity>(
    userId: string,
    bulkUploadId: string,
    options?: Omit<
      UseQueryOptions<
        BulkInvoiceUploadEntity,
        Error,
        TSelected,
        BulkUploadEntityQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const queryOptions = bulkUploadEntity.createOptions<TSelected>(
      userId,
      bulkUploadId,
      options
    );
    return useQuery<
      BulkInvoiceUploadEntity,
      Error,
      TSelected,
      BulkUploadEntityQueryKey
    >({
      ...queryOptions,
      enabled: options?.enabled ?? (!!userId && !!bulkUploadId),
    });
  },
};
